/**
 * useMaterialBreaks - Material break zone state management
 *
 * A material break is a 2D zone on a wall face with:
 * - Height range (bottomHeight → topHeight in mm from floor)
 * - Material reference
 * - Wall face assignment (which wall, inner or outer side)
 *
 * Example: tile splash zone behind kitchen counter
 *   bottomHeight: 0, topHeight: 900, materialRef: 'MAT_FLIESE', side: 'inner'
 */

import { useState, useCallback, useMemo } from 'react';

let breakIdCounter = 0;
function generateBreakId() {
  return `mb_${Date.now()}_${++breakIdCounter}`;
}

/**
 * Hook for managing material break zones
 * @param {Object} wallState - Reference to useWallState
 * @returns {Object} Material break state and operations
 */
export function useMaterialBreaks(wallState = null) {
  const [materialBreaks, setMaterialBreaks] = useState([]);

  // ============= CREATE =============

  /**
   * Create a material break zone on a wall face
   * @param {Object} options
   * @param {string} options.wallId - Wall ID
   * @param {string} options.side - 'inner' | 'outer'
   * @param {number} options.bottomHeight - Bottom of zone in mm from floor
   * @param {number} options.topHeight - Top of zone in mm from floor
   * @param {string} options.materialRef - Material reference ID (e.g. 'MAT_FLIESE')
   * @param {string} options.layerSetId - Optional LayerSet ID
   * @param {number} options.startPosition - Start along wall (0-1), default 0
   * @param {number} options.endPosition - End along wall (0-1), default 1
   * @param {string} options.name - Display name
   * @returns {Object} Created material break
   */
  const createMaterialBreak = useCallback((options) => {
    const mb = {
      id: generateBreakId(),
      wallId: options.wallId,
      side: options.side || 'inner',
      bottomHeight: options.bottomHeight ?? 0,
      topHeight: options.topHeight ?? 900,
      materialRef: options.materialRef || null,
      layerSetId: options.layerSetId || null,
      startPosition: options.startPosition ?? 0,  // 0-1 along wall
      endPosition: options.endPosition ?? 1,       // 0-1 along wall
      name: options.name || 'Materialbruch',
      createdAt: Date.now(),
      modifiedAt: Date.now(),
    };

    setMaterialBreaks(prev => [...prev, mb]);
    return mb;
  }, []);

  // ============= UPDATE =============

  const updateMaterialBreak = useCallback((id, updates) => {
    setMaterialBreaks(prev => prev.map(mb =>
      mb.id === id ? { ...mb, ...updates, modifiedAt: Date.now() } : mb
    ));
  }, []);

  // ============= DELETE =============

  const deleteMaterialBreak = useCallback((id) => {
    setMaterialBreaks(prev => prev.filter(mb => mb.id !== id));
  }, []);

  /**
   * Delete all material breaks for a wall (called when wall is deleted)
   */
  const deleteBreaksForWall = useCallback((wallId) => {
    setMaterialBreaks(prev => prev.filter(mb => mb.wallId !== wallId));
  }, []);

  // ============= QUERIES =============

  const getMaterialBreak = useCallback((id) => {
    return materialBreaks.find(mb => mb.id === id) || null;
  }, [materialBreaks]);

  /**
   * Get all material breaks for a specific wall
   */
  const getBreaksForWall = useCallback((wallId) => {
    return materialBreaks.filter(mb => mb.wallId === wallId);
  }, [materialBreaks]);

  /**
   * Get material breaks for a wall face (wall + side)
   */
  const getBreaksForWallFace = useCallback((wallId, side) => {
    return materialBreaks.filter(mb => mb.wallId === wallId && mb.side === side);
  }, [materialBreaks]);

  // ============= QUANTITIES =============

  /**
   * Calculate area for each material break zone
   */
  const breakQuantities = useMemo(() => {
    const walls = wallState?.walls || [];

    return materialBreaks.map(mb => {
      const wall = walls.find(w => w.id === mb.wallId);
      if (!wall) return { breakId: mb.id, area: 0, areaM2: 0 };

      const baseStart = wall.baseStart;
      const baseEnd = wall.baseEnd;
      const dx = baseEnd.x - baseStart.x;
      const dy = baseEnd.y - baseStart.y;
      const wallLength = Math.sqrt(dx * dx + dy * dy);

      // Zone length along wall
      const zoneLength = wallLength * (mb.endPosition - mb.startPosition);
      // Zone height
      const zoneHeight = mb.topHeight - mb.bottomHeight;
      // Area
      const area = zoneLength * zoneHeight;

      return {
        breakId: mb.id,
        wallId: mb.wallId,
        materialRef: mb.materialRef,
        area,           // mm²
        areaM2: area / 1e6, // m²
        zoneLength,     // mm
        zoneHeight,     // mm
      };
    });
  }, [materialBreaks, wallState?.walls]);

  // ============= SERIALIZATION =============

  const toJSON = useCallback(() => ({
    materialBreaks: materialBreaks.map(mb => ({ ...mb })),
  }), [materialBreaks]);

  const fromJSON = useCallback((json) => {
    if (json.materialBreaks) setMaterialBreaks(json.materialBreaks);
  }, []);

  const clear = useCallback(() => {
    setMaterialBreaks([]);
  }, []);

  return {
    materialBreaks,
    createMaterialBreak,
    updateMaterialBreak,
    deleteMaterialBreak,
    deleteBreaksForWall,
    getMaterialBreak,
    getBreaksForWall,
    getBreaksForWallFace,
    breakQuantities,
    toJSON,
    fromJSON,
    clear,
  };
}

export default useMaterialBreaks;
